import React, { useEffect, useState, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Youtube, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV } from '@/constants/testIds';
import { InlineNewsletter } from '@/components/InlineNewsletter';
import { api } from '@/lib/api';
import { MarketingBanner } from '@/components/Marketing';

const FALLBACK_CHANNELS = [
  { route: '/watch', label: 'Watch', accent: 'pink' },
  { route: '/music', label: 'Music', accent: 'emerald' },
  { route: '/podcasts', label: 'Podcasts', accent: 'emerald' },
  { route: '/on-my-mind', label: 'On My Mind', accent: 'gold' },
  { route: '/mobile-reads', label: 'Mobile Reads', accent: 'gold' },
  { route: '/meet-g', label: 'Meet G', accent: 'gold' },
  { route: '/collab', label: 'Collab with G', accent: 'gold' },
];

function LogoMark({ size = 38 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className="shrink-0" aria-hidden>
      <defs>
        <radialGradient id="glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E2C77A" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#E2C77A" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="22" stroke="#E2C77A" strokeOpacity="0.45" strokeWidth="1" />
      <ellipse cx="32" cy="32" rx="30" ry="10" stroke="#E2C77A" strokeOpacity="0.65" strokeWidth="1" transform="rotate(28 32 32)" />
      <ellipse cx="32" cy="32" rx="30" ry="10" stroke="#E2C77A" strokeOpacity="0.65" strokeWidth="1" transform="rotate(-28 32 32)" />
      <text x="32" y="40" textAnchor="middle" fontFamily="Gloock, serif" fontSize="22" fill="#E2C77A">G</text>
      <circle cx="32" cy="32" r="3" fill="url(#glow)" />
    </svg>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [channels, setChannels] = useState(FALLBACK_CHANNELS);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    api.get('/channels').then(({ data }) => {
      const navItems = (data || []).filter((c) => c.show_in_nav).map((c) => ({
        route: c.route, label: c.label, accent: c.accent || 'emerald', key: c.key,
      }));
      if (navItems.length > 0) setChannels(navItems);
    }).catch(() => {});
  }, []);

  const testidFor = (c) => {
    const map = {
      '/watch': NAV.navWatch,
      '/music': 'nav-music',
      '/podcasts': 'nav-podcasts',
      '/on-my-mind': 'nav-on-my-mind',
      '/mobile-reads': 'nav-mobile-reads',
      '/meet-g': 'nav-meet-g',
      '/collab': 'nav-collab',
    };
    return map[c.route] || `nav-${c.key || (c.label || '').toLowerCase().replace(/\s+/g, '-')}`;
  };

  const accentClass = (acc) => acc === 'emerald' ? 'pg-text-glow-emerald' : acc === 'gold' ? 'pg-text-glow-gold' : 'pg-text-glow-pink';

  return (
    <header
      data-testid={NAV.global}
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
        scrolled ? 'bg-[rgba(7,8,15,0.88)] backdrop-blur-md border-b border-[var(--pg-border-2)]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group shrink-0">
          <LogoMark size={34} />
          <span className="font-display text-xl tracking-[0.18em] text-[var(--pg-gold-2)] hidden sm:inline">PLANET&nbsp;G</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 lg:gap-12">
          {channels.map((c) => (
            <NavLink
              key={c.route}
              to={c.route}
              data-testid={testidFor(c)}
              className={({ isActive }) =>
                `relative font-display text-[15px] tracking-[0.22em] transition-colors ${
                  isActive ? accentClass(c.accent) : 'text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {c.label.toUpperCase()}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1.5 left-0 right-0 h-[2px]"
                      style={{
                        background: c.accent === 'emerald' ? 'var(--pg-emerald)' : c.accent === 'gold' ? 'var(--pg-gold-2)' : 'var(--pg-neon-pink)',
                        boxShadow: `0 0 12px ${c.accent === 'emerald' ? 'rgba(25,194,142,0.7)' : c.accent === 'gold' ? 'rgba(226,199,122,0.7)' : 'rgba(255,62,165,0.7)'}`,
                      }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/music" data-testid={NAV.enterLounge} className="pg-btn-gold hidden md:inline-flex">
            Enter the lounge
          </Link>
          <button
            data-testid={NAV.mobileOpenButton}
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 rounded-md text-[var(--pg-ink)] hover:text-[var(--pg-gold-2)] border border-[var(--pg-border-2)]"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
          <button
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            className="hidden md:inline-flex p-2.5 rounded-md text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] border border-[var(--pg-border-2)]"
          >
            <Menu size={18} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 1, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[rgba(7,8,15,0.97)] backdrop-blur-lg border-t border-[var(--pg-border-2)]"
          >
            <div className="max-w-7xl mx-auto px-4 py-6 grid gap-3">
              {channels.map((c, i) => (
                <NavLink
                  key={c.route}
                  to={c.route}
                  data-testid={NAV.mobileRoomLink}
                  className={({ isActive }) =>
                    `flex items-baseline justify-between py-3 border-b border-[var(--pg-border-2)] ${
                      isActive ? accentClass(c.accent) : 'text-[var(--pg-ink)]'
                    }`
                  }
                >
                  <span className="font-display text-2xl tracking-[0.22em]">{String(i+1).padStart(2,'0')} · {c.label.toUpperCase()}</span>
                </NavLink>
              ))}
              <Link to="/music" className="pg-btn-gold mt-3">Enter the lounge</Link>
              <Link to="/admin/login" className="text-[var(--pg-ink-faint)] font-mono text-xs tracking-widest mt-2">caretaker access →</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export function Footer() {
  const [cfg, setCfg] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get('/footer-config')
      .then(({ data }) => { if (alive) setCfg(data); })
      .catch(() => { /* fallback to hardcoded legacy footer below */ });
    return () => { alive = false; };
  }, []);

  // If footer is admin-disabled, render nothing.
  if (cfg && cfg.enabled === false) return null;

  // Loading / offline fallback → keep existing legacy footer (preserves current UX during load).
  const showNewsletter = !cfg || cfg.show_newsletter;
  const showSocial = !cfg || cfg.show_social;
  const showCopyright = !cfg || cfg.show_copyright;
  const copyrightText = cfg?.copyright_text || `\u00a9 ${new Date().getFullYear()} Planet G \u2014 a channel for the curious.`;

  const socialLinks = (cfg?.company?.social_links) || [];
  const primaryLinks = (cfg?.links || []).filter((l) => l.section === 'primary' || l.section === 'secondary');
  const legalLinks   = (cfg?.links || []).filter((l) => l.section === 'legal' || l.kind === 'legal');
  const socialLinksExplicit = (cfg?.links || []).filter((l) => l.section === 'social' || l.kind === 'social');

  // Icon lookup for social platforms → reuse existing lucide/svg
  const socialIcon = (platform) => {
    const key = (platform || '').toLowerCase();
    if (key === 'instagram') return <Instagram size={16} />;
    if (key === 'youtube') return <Youtube size={16} />;
    if (key === 'twitter') return <span className="text-sm font-bold">X</span>;
    if (key === 'spotify') return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34a.75.75 0 0 1-1.03.245c-2.823-1.722-6.378-2.111-10.565-1.156a.749.749 0 1 1-.331-1.461c4.583-1.045 8.512-.598 11.683 1.342a.75.75 0 0 1 .243 1.03zm1.47-3.272a.937.937 0 0 1-1.286.308c-3.231-1.985-8.156-2.56-11.978-1.4a.938.938 0 0 1-.544-1.794c4.366-1.323 9.793-.682 13.5 1.6a.937.937 0 0 1 .308 1.286zm.126-3.403C15.24 8.4 8.97 8.198 5.395 9.283a1.124 1.124 0 1 1-.652-2.151c4.103-1.246 11.027-1.005 15.378 1.585a1.124 1.124 0 0 1-1.144 1.948z"/></svg>;
    return <Mail size={16} />;
  };

  return (
    <footer className="mt-24 border-t border-[var(--pg-border-2)] bg-[rgba(7,8,15,0.7)]" data-testid="global-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid gap-6 lg:grid-cols-3 items-start">
        <div>
          {showNewsletter ? (
            <>
              <p className="pg-text-glow-emerald font-mono text-[11px] tracking-[0.28em]">STAY CONNECTED</p>
              <p className="text-[var(--pg-ink-muted)] text-sm mt-2">
                {cfg?.company?.footer_text || 'New drops. Exclusive content. No spam.'}
              </p>
            </>
          ) : (
            <p className="pg-text-glow-emerald font-mono text-[11px] tracking-[0.28em]">
              {cfg?.company?.brand_name?.toUpperCase() || 'PLANET G'}
            </p>
          )}
        </div>

        <div className="max-w-md w-full lg:justify-self-center">
          {showNewsletter ? (
            <InlineNewsletter ctaLabel="Subscribe" />
          ) : primaryLinks.length > 0 ? (
            <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm justify-center">
              {primaryLinks.map((l) => {
                const isExternalHref = /^(mailto:|tel:|https?:\/\/)/.test(l.href || '');
                const useAnchor = l.kind === 'external' || l.kind === 'social' || isExternalHref;
                return (
                  <li key={l.id || l.label}>
                    {useAnchor ? (
                      <a
                        href={l.href}
                        target={l.kind === 'external' ? '_blank' : undefined}
                        rel={l.kind === 'external' ? 'noopener noreferrer' : undefined}
                        data-testid={`footer-primary-link-${l.label}`}
                        className="text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] transition-colors"
                      >{l.label}</a>
                    ) : (
                      <Link to={l.href.startsWith('/') ? l.href : `/${l.href}`} data-testid={`footer-primary-link-${l.label}`} className="text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] transition-colors">{l.label}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>

        {showSocial && (
          <div className="flex items-center gap-3 lg:justify-self-end flex-wrap">
            {(socialLinks.length > 0 ? socialLinks : socialLinksExplicit.map((l) => ({ platform: 'custom', url: l.href, label: l.label }))).map((s, i) => (
              <a key={i}
                 href={s.url || '#'}
                 target="_blank"
                 rel="noopener noreferrer"
                 aria-label={s.label || s.platform}
                 className="h-10 w-10 rounded-full border border-[rgba(226,199,122,0.55)] flex items-center justify-center text-[var(--pg-gold-2)] hover:bg-[rgba(226,199,122,0.10)] transition-colors">
                {socialIcon(s.platform)}
              </a>
            ))}
            {(cfg?.company?.contact_email) && (
              <a href={`mailto:${cfg.company.contact_email}`} aria-label="Email"
                 className="h-10 w-10 rounded-full border border-[rgba(226,199,122,0.55)] flex items-center justify-center text-[var(--pg-gold-2)] hover:bg-[rgba(226,199,122,0.10)] transition-colors">
                <Mail size={16} />
              </a>
            )}
          </div>
        )}
      </div>

      {(legalLinks.length > 0 || primaryLinks.length > 0) && showNewsletter && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-4">
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-mono tracking-widest text-[var(--pg-ink-faint)] justify-center lg:justify-start">
            {primaryLinks.map((l) => {
              const isExternalHref = /^(mailto:|tel:|https?:\/\/)/.test(l.href || '');
              const useAnchor = l.kind === 'external' || l.kind === 'social' || isExternalHref;
              return (
                <li key={l.id || l.label}>
                  {useAnchor ? (
                    <a
                      href={l.href}
                      target={l.kind === 'external' ? '_blank' : undefined}
                      rel={l.kind === 'external' ? 'noopener noreferrer' : undefined}
                      data-testid={`footer-secondary-link-${l.label}`}
                      className="hover:text-[var(--pg-ink)] transition-colors"
                    >{l.label}</a>
                  ) : (
                    <Link to={l.href.startsWith('/') ? l.href : `/${l.href}`} data-testid={`footer-secondary-link-${l.label}`} className="hover:text-[var(--pg-ink)] transition-colors">{l.label}</Link>
                  )}
                </li>
              );
            })}
            {legalLinks.map((l) => (
              <li key={l.id || l.label} data-testid={`footer-legal-${l.href}`}>
                <Link to={l.href.startsWith('/') ? l.href : `/${l.href}`} className="hover:text-[var(--pg-ink)] transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="pg-hairline" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {showCopyright ? (
          <p className="text-xs text-[var(--pg-ink-faint)] font-mono" data-testid="footer-copyright">{copyrightText}</p>
        ) : <span />}
        <Link to="/admin/login" className="text-xs text-[var(--pg-ink-faint)] hover:text-[var(--pg-ink)] transition-colors font-mono tracking-widest">caretaker access</Link>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const [banner, setBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    api.get('/marketing/active').then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      const found = list.find((p) => p.placement === 'banner');
      if (!found) { setBanner(null); return; }
      // Honor dismiss frequency via sessionStorage / localStorage
      try {
        const sessionKey = `pg_marketing_dismissed::${found.id}::session`;
        const dayKey = `pg_marketing_dismissed::${found.id}::day`;
        const today = new Date().toISOString().slice(0,10);
        if (found.frequency === 'once_per_session' && sessionStorage.getItem(sessionKey)) return;
        if (found.frequency === 'once_per_day') {
          const raw = localStorage.getItem(dayKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.day === today) return;
          }
        }
      } catch (e) { /* noop */ }
      setBanner(found);
    }).catch(() => {});
  }, []);

  const dismissBanner = useCallback(() => {
    if (!banner) return;
    setBannerDismissed(true);
    try {
      if (banner.frequency === 'once_per_session') {
        sessionStorage.setItem(`pg_marketing_dismissed::${banner.id}::session`, '1');
      } else if (banner.frequency === 'once_per_day') {
        const today = new Date().toISOString().slice(0,10);
        localStorage.setItem(`pg_marketing_dismissed::${banner.id}::day`, JSON.stringify({ day: today, ts: Date.now() }));
      }
    } catch (e) { /* noop */ }
  }, [banner]);

  return (
    <div className="min-h-screen flex flex-col">
      <AnimatePresence>
        {banner && !bannerDismissed && (
          <MarketingBanner key={banner.id} poster={banner} onDismiss={dismissBanner} />
        )}
      </AnimatePresence>
      <Header />
      <main className="flex-1 pt-20 pb-32">{children}</main>
      <Footer />
    </div>
  );
}
